import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { ApiError, api, apiFetch, ensureCsrfCookie, isApiError } from './api'

/**
 * 认证域。后端契约见 docs/plans/feat-008-tanstack-crm-demo.md §后端 API 面:
 *   GET  /api/v1/auth/csrf    ensure_csrf_cookie,种 csrftoken
 *   POST /api/v1/auth/login   {username, password} → 当前用户
 *   POST /api/v1/auth/logout
 *   GET  /api/v1/auth/me      未登录返回 401
 */

/**
 * demo 账号(seed 创建),登录页提示用。
 *
 * ★ **退场判据(I7 / PT-12,本轮未满足)**:凭据提示改由后端 `GET /auth/csrf` additive 下发
 *   (默认只在 `DEBUG` 下给),届时删掉本常量、登录页改读响应字段 —— 于是准生产部署不必
 *   靠改前端代码来藏演示账号。**当前 `CsrfResponse` 只有 `{detail, csrf_token}`**
 *   (见 `packages/api-client/.../csrfResponse.ts`),字段不存在 ⇒ 前端半无从落地,本轮跳过。
 */
export const DEMO_CREDENTIALS = { username: 'demo', password: 'gongkar-demo-2026' } as const

/**
 * 宽松解析:后端未定字段照单收下,只强约束前端真正消费的几项。
 *
 * ★feat-008 W12(cf 呈报 3 落地):**本 schema 现在是全站 `/auth/me` 的唯一读取形状** ——
 * 此前 CRM 侧还有第二条查询键(生成 hook `useAuthMeRetrieve`)各拉一份,冷加载因此打两次
 * `/auth/me`,而 `looseObject` 把 `modules` / `role` / `profile` / `salesmartly_seat`
 * **原样带过却没有类型**,那正是当初不敢合并的原因。补上这四项的类型之后两键归一
 * (见 `lib/module-access.ts`),冷加载单请求。
 *
 * ★ 仍然**只约束消费面**,不复制后端契约:字段的**口径**真源恒在后端 serializer 的
 *   help_text 与 `packages/api-client` 的生成类型注释里,这里只写「前端要读、且必须有类型」
 *   的那几项。后端加字段不需要改这里(looseObject 照单收下),前端**开始读**才需要加一行。
 */
export const currentUserSchema = z.looseObject({
  id: z.union([z.number(), z.string()]).optional(),
  username: z.string(),
  display_name: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: z.string().nullish(),
  is_staff: z.boolean().nullish(),
  /**
   * 运行 profile:`full`(含电商域)/ `online`(上线白名单)。★feat-008 S7 起它**不再是
   * 能力退化的判据**(判据是下面的 `modules`,后端已把 profile 维度合成进去);字段保留,
   * 因为它仍是「后端跑在哪个形态」的事实(PT-17)。
   */
  profile: z.string().nullish(),
  /** ★feat-008 S7(PT-14):角色码 `sales` / `manager` / `admin`;该账号无员工建档时为 null。 */
  role: z.string().nullish(),
  /**
   * ★feat-008 S7(PT-14):当前账号可达的模块码(**后端已合成 profile 维度**)。
   *
   * `.catch([])` 是**总函数化**而不是兜底口径:老后端没有这个键时给空数组,让「清单到手
   * 与否」这个判断收敛到 `user === undefined` 一处(见 `module-access.ts` 的 `useAuthMe`)。
   * ⚠️ 空数组是**明确答案**(该账号确实没有任何模块),不等于「还没到手」——两者混同会让
   * 无权账号看见一屏必然 403 的入口。
   */
  modules: z.array(z.string()).catch([]),
  /**
   * ★feat-008 W5(闭 G9):当前账号的 SS 席位绑定,**未绑定时整块为 null**。
   * Inbox 的「我的」筛选值取 `salesmartly_sys_user_id`;快照名只作核对锚(展示名以字典为准)。
   */
  salesmartly_seat: z
    .looseObject({
      salesmartly_sys_user_id: z.number(),
      salesmartly_display_name_snapshot: z.string(),
    })
    .nullish(),
})

export type CurrentUser = z.infer<typeof currentUserSchema>

/** 兼容后端可能的两种包法:裸用户对象 或 {user: {...}}。 */
function pickUserCandidate(raw: unknown): unknown {
  if (raw && typeof raw === 'object' && 'user' in raw) {
    return (raw as { user: unknown }).user
  }
  return raw
}

function unwrapUser(raw: unknown): CurrentUser {
  return currentUserSchema.parse(pickUserCandidate(raw))
}

/** 顶栏展示名:display_name > 姓名 > username。 */
export function userDisplayName(user: CurrentUser | null | undefined): string {
  if (!user) return '未登录'
  if (user.display_name) return user.display_name
  const full = `${user.last_name ?? ''}${user.first_name ?? ''}`.trim()
  return full.length > 0 ? full : user.username
}

/** 取当前登录用户;未登录(401)返回 null 而不是抛错。 */
export async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser | null> {
  try {
    const raw = await apiFetch<unknown>('/auth/me', {
      skipUnauthorizedHandler: true,
      ...(signal ? { signal } : {}),
    })
    return unwrapUser(raw)
  } catch (error) {
    if (isApiError(error) && error.isUnauthorized) return null
    throw error
  }
}

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

/**
 * `/auth/me` 的**全站唯一查询**(★feat-008 W12:两键归一,冷加载单请求)。
 *
 * 三处都吃这一份:路由 `beforeLoad` 的登录门禁 / 顶栏与导航的模块门控(`useModuleAccess`)/
 * Inbox 的「我的坐席」。此前后两者走生成 hook `useAuthMeRetrieve` 的键,与本键各缓存一份 ——
 * 同一件事两条链路,不只是多打一次请求,还会在会话恰好在两者之间过期时给出**互相矛盾**的
 * 两份身份(一边还在吃缓存、一边已经 401)。
 *
 * ★ 取数**必须**是这里的 `fetchCurrentUser` 而不是生成的 `authMeRetrieve`:后者经
 *   `customFetch`,401 会**无条件触发全局跳登录**;而本查询有一个合法的 401 调用点 ——
 *   登录页开屏探测「是不是已经登录过了」。那里必须允许 401 静静地返回 `null`。
 *
 * `staleTime` 取 60s(两条旧链路里**更严**的那一档):它是登录门禁的重新校验节奏,
 * 放宽到模块清单那档(10 min)会让会话过期后多刷好几屏才被发现。
 */
export function currentUserQueryOptions() {
  return queryOptions({
    queryKey: authKeys.me(),
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 60_000,
    retry: false,
  })
}

export interface LoginPayload {
  username: string
  password: string
}

/** 登录:先确保 csrftoken 已种下,再带 X-CSRFToken 提交。 */
export async function login(payload: LoginPayload): Promise<CurrentUser> {
  await ensureCsrfCookie()
  const raw = await api.post<unknown>('/auth/login', payload, { skipUnauthorizedHandler: true })

  // 后端若直接回用户对象就用它;若只回 {detail: 'ok'} 之类,再拉一次 /auth/me 取身份。
  const parsed = currentUserSchema.safeParse(pickUserCandidate(raw))
  if (parsed.success) return parsed.data

  const user = await fetchCurrentUser()
  if (user) return user
  throw new ApiError({
    message: '登录接口返回成功,但会话未建立,请检查后端 session 配置',
    status: 500,
    url: '/api/v1/auth/login',
    code: 'session_not_established',
  })
}

export async function logout(): Promise<void> {
  await api.post<unknown>('/auth/logout', undefined, { skipUnauthorizedHandler: true })
}
